import React, { useEffect, useState } from "react";
import { Printer, RefreshCw, ReceiptText } from "lucide-react";
import { toast } from "sonner";
import { api, formatApiError } from "../lib/api";
import { PageHeader, Panel } from "../lib/ui";
import { Button } from "../components/ui/button";
import { Label } from "../components/ui/label";
import { Input } from "../components/ui/input";
import { Textarea } from "../components/ui/textarea";
import { Switch } from "../components/ui/switch";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../components/ui/select";
import {
  getPrinterConfig,
  isAndroidDevice,
  printReceipt,
  receiptHtml,
  receiptText,
  savePrinterConfig,
  saveReceiptConfig,
} from "../lib/qz";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "../components/ui/dialog";

const DEMO = {
  number: "SALES000000", created_at: new Date().toISOString(), customer_name: "Test Customer", created_by: "Admin Vente",
  items: [{ product_name: "Contoh Produk", color: "Nude", size: "L", qty: 2, price: 100000, total: 200000 }],
  sub_total: 200000, discount: 10000, grand_total: 190000, payment_method: "Cash", cash_received: 200000, change: 10000,
};

export default function PrinterSettings() {
  const savedPrinter = getPrinterConfig();
  const [cfg, setCfg] = useState(
    savedPrinter?.method === "qz"
      ? { method: "rawbt", width: savedPrinter.width || 58 }
      : savedPrinter || { method: "rawbt", width: 58 },
  );
  const [rcfg, setRcfg] = useState({ store_name: "VENTEDAILY", address: "", phone: "", footer: "Terima kasih telah berbelanja :)", show_shipping: false, show_tax: false, print_method: "rawbt", paper_width: 58 });
  const [savingR, setSavingR] = useState(false);
  const [previewOpen, setPreviewOpen] = useState(false);
  const useRawBtPreview = (cfg.method || rcfg.print_method) === "rawbt" && isAndroidDevice();

  useEffect(() => {
    api.get("/settings/receipt").then((r) => { setRcfg(r.data); saveReceiptConfig(r.data); }).catch(() => {});
  }, []);

  const saveReceipt = async () => {
    setSavingR(true);
    try { await api.put("/settings/receipt", rcfg); saveReceiptConfig(rcfg); toast.success("Teks struk disimpan"); }
    catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
    finally { setSavingR(false); }
  };

  const save = () => {
    savePrinterConfig(cfg); toast.success("Pengaturan printer disimpan");
  };

  const test = async () => {
    savePrinterConfig(cfg);
    saveReceiptConfig(rcfg);
    try { const mode = await printReceipt(DEMO); toast.success(mode === "rawbt" ? "Dikirim ke RawBT" : "Dialog print browser selesai"); }
    catch (e) { toast.error(e.message || "Gagal cetak"); }
  };

  return (
    <div data-testid="printer-settings-page">
      <PageHeader title="Printer" subtitle="Cetak struk dengan RawBT atau dialog browser" />

      <Panel className="p-5 mb-4">
        <div className="mb-4 max-w-xs">
          <Label>Metode Cetak</Label>
          <Select value={cfg.method || "rawbt"} onValueChange={(v) => setCfg({ ...cfg, method: v })}>
            <SelectTrigger className="bg-white" data-testid="print-method-select"><SelectValue /></SelectTrigger>
            <SelectContent>
              <SelectItem value="rawbt">RawBT (HP / Android)</SelectItem>
              <SelectItem value="browser">Dialog Browser</SelectItem>
            </SelectContent>
          </Select>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <div>
            <Label>Lebar Kertas</Label>
            <Select value={String(cfg.width)} onValueChange={(v) => setCfg({ ...cfg, width: parseInt(v) })}>
              <SelectTrigger className="bg-white" data-testid="printer-width"><SelectValue /></SelectTrigger>
              <SelectContent><SelectItem value="58">58 mm</SelectItem><SelectItem value="80">80 mm</SelectItem></SelectContent>
            </Select>
          </div>
        </div>
        <div className="flex items-center gap-2 mt-4">
          <Button onClick={save} className="bg-cocoa hover:bg-cocoa-dark text-ivory" data-testid="printer-save-btn"><Printer className="w-4 h-4 mr-1.5" />Simpan</Button>
          <Button onClick={() => setPreviewOpen(true)} variant="outline" className="border-taupe text-cocoa" data-testid="printer-preview-btn"><ReceiptText className="w-4 h-4 mr-1.5" />Preview Struk</Button>
          <Button onClick={test} variant="outline" className="border-taupe text-cocoa" data-testid="printer-test-btn"><RefreshCw className="w-4 h-4 mr-1.5" />Test Print</Button>
        </div>
      </Panel>
      <Dialog open={previewOpen} onOpenChange={setPreviewOpen}>
        <DialogContent className="max-w-md" data-testid="printer-preview-dialog">
          <DialogHeader>
            <DialogTitle className="font-heading text-cocoa">Preview Test Print</DialogTitle>
            <DialogDescription>
              Periksa seluruh isi struk sebelum mengirim test print.
            </DialogDescription>
          </DialogHeader>
          <div className="flex justify-center bg-taupe/10 p-4 rounded-lg max-h-[62vh] overflow-auto">
            {useRawBtPreview ? (
              <pre
                className="w-full max-w-[360px] bg-white p-3 text-[11px] leading-tight text-black"
                data-testid="printer-preview-rawbt"
              >
                {receiptText(DEMO, (cfg.width || rcfg.paper_width || 58) >= 80 ? 48 : 32)}
              </pre>
            ) : <iframe
              title="preview-test-struk"
              srcDoc={receiptHtml(DEMO, cfg.width || rcfg.paper_width || 58)}
              style={{
                width: `${Math.round((cfg.width || rcfg.paper_width || 58) * 3.78)}px`,
                height: "58vh",
                border: "1px solid #e5ddd3",
                background: "#fff",
              }}
              data-testid="printer-preview-frame"
            />}
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setPreviewOpen(false)} data-testid="printer-preview-close">Tutup</Button>
            <Button onClick={test} data-testid="printer-preview-test-print" className="bg-cocoa text-ivory"><Printer className="w-4 h-4 mr-1.5" />Test Print yang Sama</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <Panel className="p-5 mb-4">
        <div className="flex items-center gap-2 mb-4"><ReceiptText className="w-5 h-5 text-cocoa" /><h3 className="font-heading text-cocoa text-lg">Kustom Teks Struk</h3></div>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <div><Label>Nama Toko</Label><Input value={rcfg.store_name || ""} onChange={(e) => setRcfg({ ...rcfg, store_name: e.target.value })} className="bg-white" data-testid="receipt-store-name" /></div>
          <div><Label>Telepon</Label><Input value={rcfg.phone || ""} onChange={(e) => setRcfg({ ...rcfg, phone: e.target.value })} className="bg-white" placeholder="0812-xxxx-xxxx" data-testid="receipt-phone" /></div>
          <div className="md:col-span-2"><Label>Alamat <span className="text-xs text-wmuted font-normal">(satu baris = satu baris di struk)</span></Label><Textarea value={rcfg.address || ""} onChange={(e) => setRcfg({ ...rcfg, address: e.target.value })} rows={2} className="bg-white" placeholder={"Betung Trade Center\nLantai 1 Blok A4 no 14"} data-testid="receipt-address" /></div>
          <div className="md:col-span-2"><Label>Catatan Kaki (Footer)</Label><Textarea value={rcfg.footer || ""} onChange={(e) => setRcfg({ ...rcfg, footer: e.target.value })} rows={3} className="bg-white" placeholder={"Periksa barang Anda terlebih dahulu.\nPenukaran max 3x24 jam."} data-testid="receipt-footer" /></div>
        </div>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mt-4">
          <div><Label>Metode Cetak Default <span className="text-xs text-wmuted font-normal">(berlaku semua device)</span></Label>
            <Select value={rcfg.print_method || "browser"} onValueChange={(v) => setRcfg({ ...rcfg, print_method: v })}>
              <SelectTrigger className="bg-white" data-testid="receipt-print-method"><SelectValue /></SelectTrigger>
              <SelectContent>
                <SelectItem value="rawbt">RawBT (HP / Android) — langsung tanpa dialog</SelectItem>
                <SelectItem value="browser">Dialog Browser</SelectItem>
              </SelectContent>
            </Select>
          </div>
          <div><Label>Lebar Kertas Default</Label>
            <Select value={String(rcfg.paper_width || 58)} onValueChange={(v) => setRcfg({ ...rcfg, paper_width: parseInt(v) })}>
              <SelectTrigger className="bg-white" data-testid="receipt-paper-width"><SelectValue /></SelectTrigger>
              <SelectContent><SelectItem value="58">58 mm</SelectItem><SelectItem value="80">80 mm</SelectItem></SelectContent>
            </Select>
          </div>
        </div>
        <p className="text-xs text-wmuted mt-2">Pilih <b>RawBT</b> agar struk langsung terkirim ke printer via aplikasi RawBT di HP/Android <b>tanpa dialog</b> & tanpa mencetak halaman. Pastikan aplikasi <b>RawBT</b> sudah terpasang di HP.</p>
        <div className="flex flex-wrap items-center gap-6 mt-4">
          <label className="flex items-center gap-2 text-sm text-cocoa"><Switch checked={!!rcfg.show_shipping} onCheckedChange={(v) => setRcfg({ ...rcfg, show_shipping: v })} data-testid="receipt-show-shipping" /> Tampilkan baris Pengiriman</label>
          <label className="flex items-center gap-2 text-sm text-cocoa"><Switch checked={!!rcfg.show_tax} onCheckedChange={(v) => setRcfg({ ...rcfg, show_tax: v })} data-testid="receipt-show-tax" /> Tampilkan baris Pajak</label>
        </div>
        <div className="flex items-center gap-2 mt-4">
          <Button onClick={saveReceipt} disabled={savingR} className="bg-cocoa hover:bg-cocoa-dark text-ivory" data-testid="receipt-save-btn"><ReceiptText className="w-4 h-4 mr-1.5" />{savingR ? "Menyimpan..." : "Simpan Teks Struk"}</Button>
          <Button onClick={test} variant="outline" className="border-taupe text-cocoa" data-testid="receipt-preview-btn"><RefreshCw className="w-4 h-4 mr-1.5" />Test Print</Button>
        </div>
      </Panel>

      <Panel className="p-5 text-sm text-cocoa/80 space-y-2">
        <div className="font-semibold text-cocoa">Cara setup</div>
        <ol className="list-decimal ml-5 space-y-1">
          <li>Install aplikasi <b>RawBT</b> di HP Android dan sambungkan printer thermal di aplikasi tersebut.</li>
          <li>Pilih RawBT serta lebar kertas printer (58 mm atau 80 mm), lalu klik <b>Simpan</b>.</li>
          <li>Gunakan <b>Preview Struk</b>, lalu klik <b>Test Print yang Sama</b> untuk mengirim format itu ke RawBT.</li>
          <li>Untuk desktop atau iOS, pilih Dialog Browser dan atur ukuran kertas pada dialog cetak.</li>
        </ol>
        <p className="text-xs text-wmuted">RawBT hanya berjalan pada Android. Di perangkat lain sistem membuka dialog print browser.</p>
      </Panel>
    </div>
  );
}
